import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import PrintModal from '../../../../components/PrintModal';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatCurrency, formatQuantity, formatNumber } from '../../../../utils/formatters';
import { usePermesso } from '../../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../../hooks/useBloccoRecordContesto';
import { useDirtyForm } from '../../../../hooks/useDirtyForm';
import { API_DOCS } from './SchedaFattura.api';
import './SchedaFattura.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoFattura = 'IMMEDIATA' | 'DIFFERITA';
type StatoFattura = 'BOZZA' | 'EMESSA' | 'ANNULLATA';
type TipoRiga = 'DDT' | 'SAL' | 'ACCONTO' | 'STORNO_ACCONTO' | 'DESCRITTIVA';

interface RigaFattura {
  id: number;
  ordinePos: number;
  tipoRiga: TipoRiga;
  descrizione: string;
  umVendita: string | null;
  quantita: number | string | null;
  prezzoUnitario: number | string | null;
  imponibile: number | string;
  aliquotaIvaFreeze: number | string | null;
  iva: number | string;
  riferimento: string | null;
  ddt: { id: number; numero: string | null } | null;
  sal: { id: number; numero: string | null } | null;
  proformaAcconto: { id: number; numero: string | null } | null;
  codiceIva: { id: number; codice: string; aliquota: number | string } | null;
}

interface LogEntry {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface FatturaDettaglio {
  id: number;
  societaId: number;
  sedeId: number | null;
  nominativoId: number | null;
  commessaId: number | null;
  tipoFattura: TipoFattura;
  stato: StatoFattura;
  numero: string | null;
  dataDocumento: string | null;
  dataEmissione: string | null;
  note: string | null;
  totaleImponibile: number | string;
  totaleIva: number | string;
  totale: number | string;
  clienteRagioneSociale: string | null;
  clienteNome: string | null;
  clienteCognome: string | null;
  clientePartitaIva: string | null;
  clienteCodiceFiscale: string | null;
  clienteIndirizzo: string | null;
  clienteCap: string | null;
  clienteCitta: string | null;
  clienteProvincia: string | null;
  clienteEmail: string | null;
  clienteTelefono: string | null;
  commessa: { id: number; numero: string; descrizione: string | null } | null;
  tipoDocumento: { id: number; codice: string; nome: string } | null;
  condizionePagamento: { id: number; nome: string } | null;
  createdBy: { id: number; nome: string; cognome?: string | null } | null;
  annullataDa: { id: number; nome: string; cognome?: string | null } | null;
  annullataIl?: string | null;
  createdAt?: string;
  righe: RigaFattura[];
  logs: LogEntry[];
}

interface TipoDocumentoOption {
  id: number;
  codice: string;
  nome: string;
}

interface PagamentoOption {
  id: number;
  nome: string;
}

// Form testata modificabile in bozza
interface TestataForm {
  tipoDocumentoId: string;
  dataDocumento: string;
  condizionePagamentoId: string;
  note: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const TIPO_RIGA: Record<TipoRiga, { label: string; cls: string }> = {
  DDT:            { label: 'DDT',            cls: 'bop-badge-info' },
  SAL:            { label: 'SAL',            cls: 'bop-badge-warning' },
  ACCONTO:        { label: 'Acconto',        cls: 'bop-badge-neutral' },
  STORNO_ACCONTO: { label: 'Storno acconto', cls: 'bop-badge-danger' },
  DESCRITTIVA:    { label: 'Descrittiva',    cls: 'bop-badge-neutral' },
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

function formatDateTimeIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('it-IT')} ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
}

function nomeUtente(u: { nome: string; cognome?: string | null } | null): string | null {
  if (!u) return null;
  return `${u.nome || ''} ${u.cognome || ''}`.trim() || null;
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function toDateInput(s: string | null | undefined): string {
  if (!s) return '';
  return s.slice(0, 10);
}

function testataFromServer(f: FatturaDettaglio): TestataForm {
  return {
    tipoDocumentoId: f.tipoDocumento ? String(f.tipoDocumento.id) : '',
    dataDocumento: toDateInput(f.dataDocumento),
    condizionePagamentoId: f.condizionePagamento ? String(f.condizionePagamento.id) : '',
    note: f.note || '',
  };
}

function renderTipoRiga(tipo: TipoRiga): React.ReactNode {
  const cfg = TIPO_RIGA[tipo] ?? { label: tipo, cls: 'bop-badge-neutral' };
  return <span className={`bop-badge ${cfg.cls}`}>{cfg.label}</span>;
}

// Campo label/valore in sola lettura
function Campo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="fatt-scheda__campo">
      <span className="fatt-scheda__campo-label">{label}</span>
      <span className="fatt-scheda__campo-value">
        {value === null || value === undefined || value === '' ? (
          <span className="bop-text-muted">—</span>
        ) : (
          value
        )}
      </span>
    </div>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function SchedaFattura() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermesso();

  const [fattura, setFattura] = useState<FatturaDettaglio | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  // Editing testata (solo bozza)
  const [testata, setTestata] = useState<TestataForm | null>(null);
  const [testataSnapshot, setTestataSnapshot] = useState('');

  // Opzioni select (solo bozza)
  const [tipiDocumento, setTipiDocumento] = useState<TipoDocumentoOption[]>([]);
  const [pagamenti, setPagamenti] = useState<PagamentoOption[]>([]);

  const [salvando, setSalvando] = useState(false);
  const [azioneInCorso, setAzioneInCorso] = useState(false);

  const etichetta = fattura ? (fattura.numero ?? 'bozza') : '';
  const isBozza = fattura?.stato === 'BOZZA';
  const isEmessa = fattura?.stato === 'EMESSA';
  const canEdit = can('commerciale.fatture_attive', 'edit');
  const canDelete = can('commerciale.fatture_attive', 'delete');
  const editabile = isBozza && canEdit;

  useBloccoRecordContesto({
    recordSocietaId: fattura?.societaId,
    label: fattura ? `Fattura ${fattura.numero ?? 'bozza'}` : 'Fattura',
  });

  const testataDirty =
    testataSnapshot !== '' && testata !== null && JSON.stringify(testata) !== testataSnapshot;
  useDirtyForm(editabile && testataDirty, fattura ? `Fattura ${etichetta}` : 'Fattura');

  // Stampa / invio: motore di stampa, modulo FATTURA
  const [modalStampa, setModalStampa] = useState(false);
  const apriStampa = () => {
    if (editabile && testataDirty) {
      showToast('Salva le modifiche prima di stampare la fattura', 'error');
      return;
    }
    setModalStampa(true);
  };

  // ── Caricamento ─────────────────────────────────────────────────────────────

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      // @api GET /api/fatture/:id -> {"success":true,"data":{"id":"number","societaId":"number","sedeId":"number|null","nominativoId":"number|null","commessaId":"number|null","tipoFattura":"IMMEDIATA|DIFFERITA","stato":"BOZZA|EMESSA|ANNULLATA","numero":"string|null","dataDocumento":"string|null","dataEmissione":"string|null","note":"string|null","totaleImponibile":"number","totaleIva":"number","totale":"number","clienteRagioneSociale":"string|null","clienteNome":"string|null","clienteCognome":"string|null","clientePartitaIva":"string|null","clienteCodiceFiscale":"string|null","clienteIndirizzo":"string|null","clienteCap":"string|null","clienteCitta":"string|null","clienteProvincia":"string|null","clienteEmail":"string|null","clienteTelefono":"string|null","commessa":{"id":"number","numero":"string","descrizione":"string"},"tipoDocumento":{"id":"number","codice":"string","nome":"string"},"condizionePagamento":{"id":"number","nome":"string"},"createdBy":{"id":"number","nome":"string"},"annullataDa":{"id":"number","nome":"string"},"righe":[{"id":"number","ordinePos":"number","tipoRiga":"DDT|SAL|ACCONTO|STORNO_ACCONTO|DESCRITTIVA","descrizione":"string","umVendita":"string|null","quantita":"number|null","prezzoUnitario":"number|null","imponibile":"number","aliquotaIvaFreeze":"number|null","iva":"number","riferimento":"string|null","ddt":{"id":"number","numero":"string"},"sal":{"id":"number","numero":"string"},"proformaAcconto":{"id":"number","numero":"string"},"codiceIva":{"id":"number","codice":"string","aliquota":"number"}}],"logs":[{"id":"number","utenteName":"string","azione":"string","dettaglio":"string","createdAt":"string"}]}}
      const r = await apiFetch<{ success: boolean; data?: FatturaDettaglio; error?: string }>(
        `/api/fatture/${id}`
      );
      if (!r.success || !r.data) {
        setErrore(typeof r.error === 'string' ? r.error : 'Fattura non trovata');
        setFattura(null);
        return;
      }
      const f = r.data;
      setFattura(f);
      const form = testataFromServer(f);
      setTestata(form);
      setTestataSnapshot(JSON.stringify(form));
    } catch (err: unknown) {
      setErrore(estraiErrore(err, 'Errore nel caricamento della fattura'));
      setFattura(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  // Carica tipi documento famiglia FATTURA (solo in bozza)
  useEffect(() => {
    if (!fattura || fattura.stato !== 'BOZZA') { setTipiDocumento([]); return; }
    // @api GET /api/tipi-documento?societaId=&famiglia=FATTURA&soloAttivi=true -> {"success":true,"data":[{"id":"number","codice":"string","nome":"string"}]}
    apiFetch<{ success: boolean; data: TipoDocumentoOption[] }>(
      `/api/tipi-documento?societaId=${fattura.societaId}&famiglia=FATTURA&soloAttivi=true`
    )
      .then(r => setTipiDocumento(r.data || []))
      .catch(() => setTipiDocumento([]));
  }, [fattura]);

  // Carica condizioni di pagamento (solo in bozza)
  useEffect(() => {
    if (!fattura || fattura.stato !== 'BOZZA') { setPagamenti([]); return; }
    // @api GET /api/pagamenti?societaId={id} -> {"success":true,"data":[{"id":"number","nome":"string"}],"condizioni":[{"id":"number","nome":"string"}]}
    apiFetch<{ data?: PagamentoOption[]; condizioni?: PagamentoOption[] }>(
      `/api/pagamenti?societaId=${fattura.societaId}`
    )
      .then(r => setPagamenti(r.data || r.condizioni || []))
      .catch(() => setPagamenti([]));
  }, [fattura]);

  // Opzioni select: attivi + eventuale valore già assegnato (anche se disattivato)
  const tipiDocumentoOptions = useMemo(() => {
    const td = fattura?.tipoDocumento;
    if (!td || tipiDocumento.some(t => t.id === td.id)) return tipiDocumento;
    return [...tipiDocumento, { id: td.id, codice: td.codice, nome: td.nome }];
  }, [tipiDocumento, fattura]);

  const pagamentiOptions = useMemo(() => {
    const cp = fattura?.condizionePagamento;
    if (!cp || pagamenti.some(p => p.id === cp.id)) return pagamenti;
    return [...pagamenti, { id: cp.id, nome: cp.nome }];
  }, [pagamenti, fattura]);

  // ── Azioni header ───────────────────────────────────────────────────────────

  const emetti = async () => {
    if (!fattura) return;
    if (testataDirty) {
      showToast('Salva le modifiche prima di emettere la fattura', 'error');
      return;
    }
    const serie = fattura.tipoFattura === 'DIFFERITA' ? 'differita' : 'immediata';
    if (!window.confirm(`Emettere la fattura? Verrà assegnato il numero della serie ${serie}.`)) return;
    setAzioneInCorso(true);
    try {
      // @api POST /api/fatture/:id/emetti -> {"success":true,"data":{"stato":"EMESSA","numero":"string","dataEmissione":"string","warning":"string|null"}}
      const r = await apiFetch<{
        success: boolean;
        data?: { stato: string; numero: string; dataEmissione: string; warning: string | null };
      }>(`/api/fatture/${fattura.id}/emetti`, { method: 'POST' });
      showToast(`Fattura emessa con numero ${r.data?.numero ?? ''}`.trim(), 'success');
      if (r.data?.warning) {
        showToast(r.data.warning, 'error');
      }
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'emissione della fattura"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const annulla = async () => {
    if (!fattura) return;
    const motivo = window.prompt(
      `Annullare la fattura ${fattura.numero ?? ''}? I SAL torneranno approvati e i DDT di nuovo fatturabili.\nMotivo (opzionale):`,
      ''
    );
    if (motivo === null) return; // annullato dall'utente
    setAzioneInCorso(true);
    try {
      // @api POST /api/fatture/:id/annulla {"motivo":"string?"} -> {"success":true,"data":{"stato":"ANNULLATA"}}
      await apiFetch<{ success: boolean; data?: { stato: string } }>(
        `/api/fatture/${fattura.id}/annulla`,
        {
          method: 'POST',
          body: JSON.stringify(motivo.trim() ? { motivo: motivo.trim() } : {}),
        }
      );
      showToast(`Fattura ${fattura.numero ?? ''} annullata`.trim(), 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'annullamento della fattura"), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const elimina = async () => {
    if (!fattura) return;
    if (!window.confirm('Eliminare definitivamente la bozza della fattura?')) return;
    setAzioneInCorso(true);
    try {
      // @api DELETE /api/fatture/:id -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/fatture/${fattura.id}`, { method: 'DELETE' });
      showToast('Fattura eliminata', 'success');
      navigate('/commerciale/attivo/fatture');
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'eliminazione della fattura"), 'error');
      setAzioneInCorso(false);
    }
  };

  // ── Salvataggio testata ─────────────────────────────────────────────────────

  const setCampoTestata = (campo: keyof TestataForm, val: string) => {
    setTestata(prev => (prev ? { ...prev, [campo]: val } : prev));
  };

  const salvaTestata = async () => {
    if (!fattura || !testata) return;
    setSalvando(true);
    try {
      // @api PUT /api/fatture/:id {"dataDocumento":"string|null","condizionePagamentoId":"number|null","note":"string|null","tipoDocumentoId":"number|null"} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/fatture/${fattura.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          dataDocumento: testata.dataDocumento || null,
          condizionePagamentoId: testata.condizionePagamentoId
            ? Number(testata.condizionePagamentoId)
            : null,
          tipoDocumentoId: testata.tipoDocumentoId ? Number(testata.tipoDocumentoId) : null,
          note: testata.note.trim() || null,
        }),
      });
      showToast('Testata fattura salvata', 'success');
      setTestataSnapshot(JSON.stringify(testata));
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel salvataggio della testata'), 'error');
    } finally {
      setSalvando(false);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const badges = useMemo(() => {
    if (!fattura) return null;
    return (
      <div className="fatt-scheda__badges">
        {fattura.tipoFattura === 'DIFFERITA'
          ? <span className="bop-badge bop-badge-info">Differita</span>
          : <span className="bop-badge bop-badge-neutral">Immediata</span>}
        {fattura.stato === 'BOZZA' && <span className="bop-badge bop-badge-neutral">Bozza</span>}
        {fattura.stato === 'EMESSA' && <span className="bop-badge bop-badge-success">Emessa</span>}
        {fattura.stato === 'ANNULLATA' && <span className="bop-badge bop-badge-danger">Annullata</span>}
      </div>
    );
  }, [fattura]);

  const indirizzoCliente = (f: FatturaDettaglio): string | null => {
    const parti: string[] = [];
    if (f.clienteIndirizzo) parti.push(f.clienteIndirizzo);
    const cittaParti = [f.clienteCap, f.clienteCitta].filter(Boolean).join(' ');
    if (cittaParti) parti.push(f.clienteProvincia ? `${cittaParti} (${f.clienteProvincia})` : cittaParti);
    return parti.length ? parti.join(', ') : null;
  };

  const docOrigine = (r: RigaFattura): React.ReactNode => {
    if (r.ddt) {
      return (
        <Link className="fatt-scheda__origine-link" to={`/commerciale/attivo/ddt/${r.ddt.id}`}>
          DDT {r.ddt.numero ?? `#${r.ddt.id}`}
        </Link>
      );
    }
    if (r.sal) {
      return (
        <Link className="fatt-scheda__origine-link" to={`/commerciale/attivo/sal/${r.sal.id}`}>
          SAL {r.sal.numero ?? `#${r.sal.id}`}
        </Link>
      );
    }
    if (r.proformaAcconto) {
      return (
        <Link
          className="fatt-scheda__origine-link"
          to={`/commerciale/attivo/acconti/${r.proformaAcconto.id}`}
        >
          Proforma {r.proformaAcconto.numero ?? `#${r.proformaAcconto.id}`}
        </Link>
      );
    }
    return <span className="bop-text-muted">—</span>;
  };

  const aliquotaRiga = (r: RigaFattura): React.ReactNode => {
    const aliquota = r.aliquotaIvaFreeze ?? r.codiceIva?.aliquota ?? null;
    if (aliquota === null || aliquota === undefined || aliquota === '') {
      return <span className="bop-text-muted">—</span>;
    }
    return `${formatNumber(Number(aliquota))}%`;
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="fatt-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento fattura…</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (errore || !fattura || !testata) {
    return (
      <Layout>
        <div className="fatt-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Fattura non disponibile</h3>
              <p className="bop-empty-state__text">{errore || 'Fattura non trovata.'}</p>
              <BopBackLink to="/commerciale/attivo/fatture" label="Torna alle fatture" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const f = fattura;

  return (
    <Layout>
      <div className="fatt-scheda">
        <BopBackLink to="/commerciale/attivo/fatture" label="Torna alle fatture" />

        {/* Header */}
        <header className="fatt-scheda__header">
          <div className="fatt-scheda__breadcrumb">
            Commerciale ›{' '}
            <Link to="/commerciale/attivo/fatture">Fatture</Link> › {f.numero ?? '(bozza)'}
          </div>
          <div className="fatt-scheda__header-row">
            <div className="fatt-scheda__header-left">
              <h1 className="bop-page-title">Fattura {f.numero ?? '(bozza)'}</h1>
              {badges}
            </div>
            <div className="fatt-scheda__header-actions">
              <button
                type="button"
                className="bop-btn bop-btn-secondary"
                onClick={apriStampa}
                title="Anteprima, PDF o invio email della fattura con il template di Impostazioni → Report"
              >
                Stampa / Invia
              </button>
              {isBozza && canEdit && (
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={emetti}
                  disabled={azioneInCorso}
                >
                  Emetti fattura
                </button>
              )}
              {isBozza && canDelete && (
                <button
                  className="bop-btn bop-btn-danger"
                  onClick={elimina}
                  disabled={azioneInCorso}
                >
                  Elimina
                </button>
              )}
              {isEmessa && canEdit && (
                <button
                  className="bop-btn bop-btn-danger"
                  onClick={annulla}
                  disabled={azioneInCorso}
                >
                  Annulla fattura
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Documento */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Documento</h2>
            {editabile && testataDirty && (
              <button
                className="bop-btn bop-btn-primary bop-btn-sm"
                onClick={salvaTestata}
                disabled={salvando}
              >
                {salvando ? 'Salvataggio…' : 'Salva'}
              </button>
            )}
          </div>
          <div className="bop-card__body">
            <div className="fatt-scheda__grid">
              <Campo
                label="Tipo fattura"
                value={f.tipoFattura === 'DIFFERITA' ? 'Differita' : 'Immediata'}
              />

              {editabile ? (
                <div className="fatt-scheda__campo">
                  <span className="fatt-scheda__campo-label">Tipo documento</span>
                  <select
                    className="bop-select"
                    value={testata.tipoDocumentoId}
                    onChange={e => setCampoTestata('tipoDocumentoId', e.target.value)}
                  >
                    <option value="">— nessuno —</option>
                    {tipiDocumentoOptions.map(t => (
                      <option key={t.id} value={String(t.id)}>{t.codice} — {t.nome}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <Campo
                  label="Tipo documento"
                  value={f.tipoDocumento ? `${f.tipoDocumento.codice} — ${f.tipoDocumento.nome}` : null}
                />
              )}

              {editabile ? (
                <div className="fatt-scheda__campo">
                  <span className="fatt-scheda__campo-label">Data documento</span>
                  <input
                    type="date"
                    className="bop-input"
                    value={testata.dataDocumento}
                    onChange={e => setCampoTestata('dataDocumento', e.target.value)}
                  />
                  <span className="fatt-scheda__hint bop-text-muted">
                    Determina il mese di liquidazione IVA
                  </span>
                </div>
              ) : (
                <Campo
                  label="Data documento"
                  value={f.dataDocumento ? formatDateIT(f.dataDocumento) : null}
                />
              )}

              {editabile ? (
                <div className="fatt-scheda__campo">
                  <span className="fatt-scheda__campo-label">Condizione pagamento</span>
                  <select
                    className="bop-select"
                    value={testata.condizionePagamentoId}
                    onChange={e => setCampoTestata('condizionePagamentoId', e.target.value)}
                  >
                    <option value="">— nessuna —</option>
                    {pagamentiOptions.map(p => (
                      <option key={p.id} value={String(p.id)}>{p.nome}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <Campo
                  label="Condizione pagamento"
                  value={f.condizionePagamento ? f.condizionePagamento.nome : null}
                />
              )}

              <Campo
                label="Commessa"
                value={
                  f.commessa ? (
                    <Link to={`/commerciale/commesse/${f.commessa.id}`}>
                      {f.commessa.numero}
                      {f.commessa.descrizione ? ` — ${f.commessa.descrizione}` : ''}
                    </Link>
                  ) : null
                }
              />

              <Campo
                label="Creato da"
                value={
                  nomeUtente(f.createdBy) || f.createdAt
                    ? `${nomeUtente(f.createdBy) || '—'}${f.createdAt ? ` · ${formatDateTimeIT(f.createdAt)}` : ''}`
                    : null
                }
              />
              <Campo
                label="Emessa il"
                value={f.dataEmissione ? formatDateIT(f.dataEmissione) : null}
              />
              {f.stato === 'ANNULLATA' && (
                <Campo
                  label="Annullata da / il"
                  value={
                    nomeUtente(f.annullataDa) || f.annullataIl
                      ? `${nomeUtente(f.annullataDa) || '—'}${f.annullataIl ? ` · ${formatDateTimeIT(f.annullataIl)}` : ''}`
                      : null
                  }
                />
              )}

              {editabile ? (
                <div className="fatt-scheda__campo fatt-scheda__campo--full">
                  <span className="fatt-scheda__campo-label">Note</span>
                  <textarea
                    className="bop-input fatt-scheda__note"
                    value={testata.note}
                    onChange={e => setCampoTestata('note', e.target.value)}
                    rows={3}
                  />
                </div>
              ) : (
                <Campo label="Note" value={f.note} />
              )}
            </div>
          </div>
        </div>

        {/* Cliente (freeze) */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Cliente</h2>
          </div>
          <div className="bop-card__body">
            <div className="fatt-scheda__grid">
              <Campo
                label="Ragione sociale"
                value={
                  f.clienteRagioneSociale ||
                  `${f.clienteNome || ''} ${f.clienteCognome || ''}`.trim() ||
                  null
                }
              />
              <Campo label="Partita IVA" value={f.clientePartitaIva} />
              <Campo label="Codice fiscale" value={f.clienteCodiceFiscale} />
              <Campo label="Indirizzo" value={indirizzoCliente(f)} />
            </div>
          </div>
        </div>

        {/* Righe */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Righe</h2>
          </div>
          <div className="bop-card__body">
            {(f.righe || []).length === 0 ? (
              <div className="bop-empty-state">
                <p className="bop-empty-state__text">Nessuna riga nella fattura.</p>
              </div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Tipo riga</th>
                    <th>Descrizione</th>
                    <th>U.M.</th>
                    <th className="bop-text-right">Q.tà</th>
                    <th className="bop-text-right">Prezzo</th>
                    <th className="bop-text-right">Imponibile</th>
                    <th className="bop-text-right">IVA %</th>
                    <th className="bop-text-right">IVA</th>
                    <th>Documento origine</th>
                  </tr>
                </thead>
                <tbody>
                  {f.righe.map(r => (
                    <tr key={r.id}>
                      <td>{r.ordinePos}</td>
                      <td>{renderTipoRiga(r.tipoRiga)}</td>
                      <td>
                        <div className="fatt-scheda__descrizione">{r.descrizione}</div>
                        {r.riferimento && (
                          <div className="fatt-scheda__riferimento bop-text-muted">
                            {r.riferimento}
                          </div>
                        )}
                      </td>
                      <td>{r.umVendita || <span className="bop-text-muted">—</span>}</td>
                      <td className="bop-text-right">
                        {r.quantita !== null && r.quantita !== undefined
                          ? formatQuantity(Number(r.quantita))
                          : <span className="bop-text-muted">—</span>}
                      </td>
                      <td className="bop-text-right">
                        {r.prezzoUnitario !== null && r.prezzoUnitario !== undefined
                          ? formatCurrency(Number(r.prezzoUnitario))
                          : <span className="bop-text-muted">—</span>}
                      </td>
                      <td
                        className={`bop-text-right${Number(r.imponibile) < 0 ? ' bop-text-danger' : ''}`}
                      >
                        {formatCurrency(Number(r.imponibile))}
                      </td>
                      <td className="bop-text-right">{aliquotaRiga(r)}</td>
                      <td className="bop-text-right">{formatCurrency(Number(r.iva))}</td>
                      <td>{docOrigine(r)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fatt-scheda__tfoot-row">
                    <td colSpan={8} className="bop-text-right fatt-scheda__tfoot-label">
                      Imponibile
                    </td>
                    <td className="bop-text-right">{formatCurrency(Number(f.totaleImponibile))}</td>
                    <td></td>
                  </tr>
                  <tr className="fatt-scheda__tfoot-row">
                    <td colSpan={8} className="bop-text-right fatt-scheda__tfoot-label">
                      IVA
                    </td>
                    <td className="bop-text-right">{formatCurrency(Number(f.totaleIva))}</td>
                    <td></td>
                  </tr>
                  <tr className="fatt-scheda__tfoot-row fatt-scheda__tfoot-row--totale">
                    <td colSpan={8} className="bop-text-right fatt-scheda__tfoot-label">
                      Totale
                    </td>
                    <td className="bop-text-right">{formatCurrency(Number(f.totale))}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>

        {/* Log attività */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Log attività</h2>
          </div>
          <div className="bop-card__body">
            {(f.logs || []).length === 0 ? (
              <div className="bop-empty-state">Nessuna voce di log.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data/ora</th>
                    <th>Utente</th>
                    <th>Azione</th>
                    <th>Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {f.logs.map(l => (
                    <tr key={l.id}>
                      <td>{formatDateTimeIT(l.createdAt)}</td>
                      <td>{l.utenteName}</td>
                      <td>{l.azione}</td>
                      <td>{l.dettaglio || <span className="bop-text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {modalStampa && (
          <PrintModal
            societaId={f.societaId}
            entitaType="fattura_cliente"
            entitaId={f.id}
            entitaLabel={`Fattura ${f.numero ?? 'bozza'}`}
            onClose={() => setModalStampa(false)}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
